'use client';

import {
  PolicySummaryCard,
  type PolicySummaryData,
} from '@hyphengroup/cms-components';
import { pickDeliveryNoteHtml } from './deliveryNotes';
import type { Market } from './formatters';
import { buildPolicySummaryRows } from './resolver';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import type { CmsCountry, CmsRegion } from '$/(cms)/_modules/cmsService/types';
import type { CmsPolicySummaryCardComponent } from '$/(cms)/_modules/cmsService/types/components';
import { useClientContext } from '$/modules/context/client';
import type { TravelPolicySummary } from '$/types/policySummary';

const brandToMarket = (brand: string): Market | undefined =>
  brand === 'singsaver' || brand === 'moneyhero' ? brand : undefined;

/**
 * Map the purchase's destination codes to display labels via the CMS country /
 * region reference data (getCountries / getRegions, fetched SSR into the page
 * context). Mirrors the HubSpot success page: single-trip destinations are ISO
 * country codes -> country label; annual destinations are region enum values ->
 * region label. Absent reference data -> undefined, so the resolver falls back
 * to the raw codes.
 *
 * A code missing from the reference set keeps its raw value in both branches -
 * dropping it would silently shorten the destination row, or empty it entirely
 * when nothing resolves.
 *
 * TI-only (destinations are a travel-insurance concept; HI has none) - the
 * caller narrows on `data.vertical === 'TI'` before calling this, rather than
 * this function guarding internally (CMS-1794).
 */
export const resolveDestinationLabels = (
  data: TravelPolicySummary,
  destinations: { countries: CmsCountry[]; regions: CmsRegion[] } | undefined,
): string[] | undefined => {
  if (!destinations) return undefined;
  const codes = data.destinations ?? [];
  if (data.planType === 'SGL') {
    return codes.map(
      (code) =>
        destinations.countries.find((c) => c.iso === code)?.label ?? code,
    );
  }
  if (data.planType === 'ANL') {
    return codes.map(
      (code) =>
        destinations.regions.find((r) => r.value === code)?.label ?? code,
    );
  }
  return undefined;
};

/**
 * Data-bound wrapper for the Policy Summary Card (CMS-1794 / CMS-1821). Reads
 * the client-resolved purchase state from the page context slice and, when
 * ready, builds the presentational `PolicySummaryData` (product name, provider
 * name, per-vertical rows, delivery note). Until then it renders the card's
 * own skeleton (`summary={undefined}`) — the whole card is client-resolved, so
 * SSR always emits the skeleton (no hydration mismatch). The page-level
 * controller (WS1) handles the invalid/error state; here loading and invalid
 * both render the skeleton.
 */
export const PolicySummaryCardWrapper = (
  props: CmsPolicySummaryCardComponent,
) => {
  const { brand, lang } = useClientContext();
  const {
    pageData: { policySummary, travelDestinations },
  } = usePageDataContext();

  const market = brandToMarket(brand);

  if (
    !market ||
    !policySummary ||
    policySummary.status !== 'ready' ||
    !policySummary.data
  ) {
    return <PolicySummaryCard summary={undefined} />;
  }

  const { data, isPending } = policySummary;

  // Destinations are a TI-only concept - HI purchases simply have none, so
  // skip resolution rather than calling a TI-shaped helper with HI data.
  const destinationLabels =
    data.vertical === 'TI'
      ? resolveDestinationLabels(data, travelDestinations)
      : undefined;

  const rows = buildPolicySummaryRows({ fields: props.fields }, data, {
    lang,
    market,
    destinationLabels,
  });

  const deliveryNoteHtml = pickDeliveryNoteHtml(
    props.deliveryNotes,
    isPending ? 'manual_underwriting' : 'success',
    data.carrierCode,
  );

  const summary: PolicySummaryData = {
    title: data.productName,
    providerName: data.carrierName,
    // Provider/product image from the purchase API (content-stack, external
    // host) — rendered as a raw <img> in the card since the host is not on the
    // Next image allowlist (same handling as the apply flow). Empty string ->
    // undefined so the card falls back to the provider-name text (CMS-1821).
    // HI has no product image from the API (TI-only field) - it always falls
    // back to the provider-name text, which is the card's documented
    // behaviour for a missing/failed image (CMS-1821 acceptance criteria).
    providerLogoUrl: (data.vertical === 'TI' && data.productImage) || undefined,
    rows,
    deliveryNoteHtml,
  };

  return <PolicySummaryCard summary={summary} />;
};
